<template>
    <div class="content__page" :class="{ 'disabled': duplicateLoading || checkUpgradeLoading && checkUpgradeFields.clients }">
        <div>
            <template v-if="pageLoading">
                <PageSpinner />
            </template>

            <template v-else-if="pageLoadingErr">
                <EmptyPage state="error" @action="handleFetchClient" />
            </template>

            <template v-else>
                <div class="flex align-items-baseline justify-content-between w-100 mb-4">
                    <div class="flex align-items-center">  
                        <div class="w-100">
                            <p 
                                class="text--semi--bold text--capitalize text-gray-800 text--xl pointer"
                                @click="openDetailsModal('name')"
                            >
                                {{ currentClient.name }}
                            </p>
                            <div class="project__item project__item__flex mt--5 mb--0">
                                <span class="project__item__title text--normal mt--0 text--xs">
                                    Added on: {{ $format_date(currentClient.createdAt) }}
                                </span>
                                <span class="ml--10 mr--10 project__item__title text--normal"> | </span>
                                <div class="dropdown" :class="{ 'disabled': loading }">
                                    <div id="statusDropdown" class="pointer" data-bs-toggle="dropdown" aria-expanded="false">
                                        <div class="project__item__title flex align-items-center text--capitalize text--xs">
                                            <Status type="client" :value="currentClient.status" />
                                            <IconSvg name="chevron-down" width="12px" />
                                        </div>
                                    </div>
            
                                    <List id="status">
                                        <ListItem
                                            v-for="status in CLIENT_STATUSES" 
                                            :key="status" 
                                            @click="handleStatusUpdate(status)"
                                        >
                                            <Status type="client" :value="status" is-table />
                                        </ListItem>
                                    </List>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="relative flex align-items-center">
                        <div class="dropdown">
                            <div id="clientDetailsDropDown" class="pointer" data-bs-toggle="dropdown" aria-expanded="false">
                                <div class="invoice__details__menu">
                                    <IconSvg name="dots-horizontal-rounded" />
                                </div>
                            </div>

                            <List id="clientDetails">
                                <FormLabel title="Basic actions" label-type="sm_mono" class="pl--10 dropdown-item-label text--semi--bold" />
                                <ListItem flex pointer @click="modalAction = 'showArchiveModal'">
                                    <IconSvg name="bank" />
                                    <span class="ml--10 text--sm text-gray-800">Archive</span>
                                </ListItem>

                                <ListItem flex @click="handleDuplicateClient">
                                    <IconSvg name="duplicate" />
                                    <span class="ml--10 text--sm text-gray-800">Duplicate</span>
                                </ListItem>

                                <li><hr class="dropdown-divider dropdown--divider" style="color: #bcc1c6;"></li>

                                <FormLabel title="Client actions" label-type="sm_mono" class="pl--10 dropdown-item-label text--semi--bold" />

                                <ListItem 
                                    v-if="['potential lead', 'onboarding', 'reached out'].includes(currentClient.status)" 
                                    :disabled="fetchCalendarsState === 'loading'"
                                    @click="openCreateMeeting"
                                >
                                    <div>
                                        <div class="flex align-items-center">
                                            <IconSvg name="phone" />
                                            <span class="ml--10 text--sm text-gray-800">
                                                Schedule virtual meeting
                                            </span>
                                        </div>
                                    </div>
                                </ListItem>
                                
                                <ListItem v-if="currentClient.status === 'active'" @click="openSendCard">
                                    <IconSvg class="mr--5 flex" name="envelope" width="16px" />  
                                    <span class="text--sm text-gray-700">
                                        Send rate card
                                    </span>
                                </ListItem>

                                <ListItem v-if="currentClient.status === 'potential lead'" disabled>
                                    <div>
                                        <div class="flex align-items-center">
                                            <IconSvg name="envelope" />
                                            <span class="ml--10 text--sm text-gray-800">
                                                Send sales email
                                            </span>
                                        </div>
                                        <div class="flex justify-content-center">
                                            <span class="tag bold text--xxs tag--grey mt--5">Coming soon!</span>
                                        </div>
                                    </div>
                                </ListItem>

                                <ListItem flex @click="openCheckInModal">
                                    <IconSvg name="bell" />
                                    <span class="ml--10 text--sm text-gray-800">
                                        Check-in reminders
                                    </span>
                                </ListItem>

                                <li><hr class="dropdown-divider dropdown--divider" style="color: #bcc1c6;"></li>

                                <ListItem flex @click="modalAction = 'showDeleteModal'">
                                    <IconSvg name="delete" fill="#e04060" />
                                    <span class="ml--10 text--sm text-error-400">Delete</span>
                                </ListItem>
                            </List>
                        </div>
                    </div>
                </div>

                <div>
                    <div class="invoice__details__row">
                        <div class="invoice__details__row__left flex-1 pl--0">
                            <div class="grid gap-2">
                                <div class="invoice__details__item py-2">
                                    <div class="col-12 mb--10 flex align-items-center justify-content-between">
                                        <FormLabel title="Client information" html-for="info" label-type="title" />
                                        <BaseButton 
                                            variant="ghost" 
                                            label="Edit" 
                                            medium
                                            @submit="openDetailsModal('clientDetails')"           
                                        />
                                    </div>

                                    <div>
                                        <div class="mb--10">
                                            <div>
                                                <FormLabel title="Email" html-for="email" label-type="menu" />
                                                <p class="invoice__detail text--sm">
                                                    {{ currentClient.email || 'N/A' }}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div class="mb--10">
                                            <div>
                                                <FormLabel title="Industry" html-for="industry" label-type="menu" />
                                                <p class="text--capitalize invoice__detail text--sm">
                                                    {{ currentClient.industry || 'N/A' }}
                                                </p>
                                            </div>
                                        </div>

                                        <div class="mb--10">
                                            <div>
                                                <FormLabel title="Phone number" html-for="phoneNumber" label-type="menu" />
                                                <p class="text--capitalize invoice__detail text--sm">
                                                    {{ currentClient.phoneNumber || '-' }}
                                                </p>
                                            </div>
                                        </div>
    
                                        <div class="mb--10">
                                            <div>
                                                <FormLabel title="Country" html-for="country" label-type="menu" />
                                                <p class="text--capitalize invoice__detail text--sm">
                                                    {{ currentClient.country || "-" }}
                                                </p>
                                            </div>
                                        </div>
    
                                        <div class="mb--10">
                                            <div>
                                                <FormLabel title="City" html-for="city" label-type="menu" />
                                                <p class="text--capitalize invoice__detail text--sm">
                                                    {{ currentClient.city || "-" }}
                                                </p>
                                            </div>
                                        </div>
    
                                        <div class="mb--10">
                                            <div>
                                                <FormLabel title="State" html-for="State" label-type="menu" />
                                                <p class="text--capitalize invoice__detail text--sm">
                                                    {{ currentClient.state || "-" }}
                                                </p>
                                            </div>
                                        </div>
    
                                        <div>
                                            <div>
                                                <FormLabel title="Zip code" html-for="zipCode" label-type="menu" />
                                                <p class="text--capitalize invoice__detail text--sm">
                                                    {{ currentClient.zipCode || "-" }}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
    
                                <div class="invoice__details__item py-2">
                                    <div class="col-12 mb--5 flex align-items-center justify-content-between">
                                        <FormLabel title="Corresponding Emails" html-for="ccEmails" label-type="title" />
                                        <BaseButton 
                                            variant="ghost" 
                                            label="Edit" 
                                            bold
                                            @submit="openDetailsModal('billingEmails')"            
                                        />
                                    </div>
    
                                    <div class="email__wrap">
                                        <div v-if="!currentClient.ccEmails.length">
                                            <span class="text--sm text-gray-600">
                                                N/A
                                            </span>
                                        </div>

                                        <div v-else class="">
                                            <p v-for="email in currentClient.ccEmails" :key="email" class="sub__tag text--sm text-gray-600 mb--5">
                                                {{ email || '-' }}
                                            </p>
                                        </div>
                                    </div>
                                </div>   
                                
                                <div class="invoice__details__item py-2">
                                    <div class="col-12 flex align-items-center justify-content-between">
                                        <FormLabel title="Links" html-for="ccEmails" label-type="title" />
                                        <BaseButton 
                                            variant="ghost" 
                                            label="+ Add" 
                                            bold
                                            @submit="modalAction = 'showAddLinkModal'"            
                                        />
                                    </div>

                                    <div class="col-12 mb--10">
                                        <div>
                                            <template v-if="loadingState === 'fetch_link_loading'">
                                                <PageSpinner size="sm" />
                                            </template>
        
                                            <template v-else-if="loadingState === 'fetch_link_error'">
                                                <EmptyPage state="error" @action="handleFetchProjectLinks" />
                                            </template>
        
                                            <template v-else>
                                                <div v-if="links.length" class="mt--15 grid gap-3">
                                                    <div 
                                                        v-for="link in links" 
                                                        :key="link._id" 
                                                        class="flex align-items-center justify-content-between w-100 flex-wrap"
                                                    >
                                                        <div class="flex align-items-center">
                                                            <a 
                                                                class="project__item__title text--normal mt--0 text--sm text--underline" 
                                                                :href="link.url"
                                                                target="_blank"
                                                            >
                                                                {{ link.name }}
                                                            </a>
                                                        </div>
                                                        
                                                        <div class="flex align-items-center gap-2">
                                                            <BaseButton 
                                                                variant="ghost" 
                                                                variant-style="secondary"
                                                                prefix-icon
                                                                icon-name="edit"
                                                                @submit="openEditLinkModal(link)"
                                                            />
                                                            <BaseButton 
                                                                variant="ghost" 
                                                                variant-style="warning"
                                                                prefix-icon
                                                                icon-name="delete"
                                                                :loading="loadingState === 'delete_link_loading' && linkIdToDelete === link._id"
                                                                @submit="deleteLink(link._id)"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>            
                                            </template>    
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="pl--15 flex-2">
                            <div>
                                <NonLinkedTabs
                                    :list="clientTabList" type="pill" 
                                    is-icon
                                    @select="setClientTab"
                                >
                                    <template #nonLinkedTab="data">
                                        <NonLinkedTab :is-active="data.name === 'Notes'">
                                            <ClientNotes />
                                        </NonLinkedTab>
                                        <NonLinkedTab :is-active="data.name === 'Projects'">
                                            <ClientProjects />
                                        </NonLinkedTab>
                                        <NonLinkedTab :is-active="data.name === 'Invoices'">
                                            <ClientInvoices :current-client="currentClient" />
                                        </NonLinkedTab>
                                        <NonLinkedTab :is-active="data.name === 'Meetings'">
                                            <ClientMeetings 
                                                :current-client="currentClient" 
                                                :client-email="currentClient.email" 
                                                :current-tab="currentTab"
                                                :selected-meeting="selectedMeeting"
                                                :refresh-meeting-list="refreshMeetingList"
                                                :calendar-integrations="calendarIntegrations"
                                                @reset-refresh="resetMeetingListUpdate"
                                            />
                                        </NonLinkedTab>
                                        <NonLinkedTab :is-active="data.name === 'Documents'">
                                            <ClientDocuments 
                                                :current-tab="currentTab"
                                            />
                                        </NonLinkedTab>
                                    </template>
                                </NonLinkedTabs>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <ConfirmDeletion
            type="client" custom-msg 
            :show-modal="modalAction === 'showDeleteModal'" 
            :loading="deleteClient.loading" 
            @cancel="modalAction = ''" 
            @delete="handleDeleteClient"
        >
            <template #custom-msg>
                <p class="text--sm text-gray-600">
                    This client will be removed from all related active and non-active projects, meetings, and invoices.
                </p>

                <p class="mt--15 text--sm text-gray-600">
                    <strong>This action cannot be undone.</strong>
                </p>
            </template>
        </ConfirmDeletion>

        <ArchiveRecord 
            type="client" 
            :loading="loadingState === 'archive_loading'" 
            :show-modal="modalAction === 'showArchiveModal'" 
            @cancel="modalAction = ''" 
            @archive="handleArchiveClient"
        />

        <FollowUpEmailModal
            :show-modal="modalAction === 'showFollowUpModal'"  
            :form-data="currentClient" 
            @close="modalAction = ''" 
            @send="sendFollowUp"
        />

        <CreateClientModal
            :show-modal="modalAction === 'showEditModal'" 
            :current-client="currentClient" 
            :current-selection="editModalType"
            :loading="loadingState === 'update_loading'"
            action-type="edit" 
            @cancel="resetEditModal" 
            @complete="updateClientForm"
        />

        <UpgradeAccountModal 
            :show-modal="showUpgradeModal || showUpgradeModalAlt" 
            :type="upgradeType" 
            :custom-text="upgradeType === 'client check-ins'"
            @cancel="closeUpgradeModal"
        >
            <template #text>
                <div>
                    <template v-if="accountPlan === 'free'">
                        <p class="text--sm text-gray-600">
                            Client check-ins is a paid feature available on the <strong>Plus</strong> plans.
                        </p>
                        <p class="text--sm text-gray-600 mt--15">
                            To unlock this feature, upgrade your account in your account settings.
                        </p>
                    </template>
                </div>
            </template>
        </UpgradeAccountModal>

        <UpgradeAccountModal 
            :show-modal="modalAction === 'showUpgradeModalSendRateCard'" 
            type="rateCards" 
            custom-text
            @cancel="modalAction = ''"
        >
            <template #text>
                <div>
                    <template v-if="accountPlan === 'free' || accountPlan === 'standard'">
                        <p class="text--sm text-gray-600">
                            This is a paid feature available on the <strong>Plus</strong> plans.
                        </p>
                        <p class="text--sm text-gray-600 mt--15">
                            To unlock this feature, upgrade your account in your account settings.
                        </p>
                    </template>
                </div>
            </template>
        </UpgradeAccountModal>

        <CreateMeetingModal 
            :show-modal="modalAction === 'showScheduleMeetingModal'" 
            action="add"
            modal-type="clients"
            :selected-meeting="selectedMeeting"
            :client-list-prop="[currentClient]"
            :calendars="calendarIntegrations" 
            @close="closeMeetingModal"
            @create-complete="onCompleteCreateMeeting" 
        />

        <MainModal 
            :show-modal="modalAction === 'showConnectModal'" 
            size="sm" 
            title="Connect your calendar"
            @close="modalAction = ''"
        >
            <template #body>
                <div class="modal__wrapper--onboarding">
                    <div class="modal--section">
                        <div class="">
                            <p class="text--sm">
                                You have no existng calendar integrations. To schedule meetings with clients, add a calendar tool to your account.
                            </p>
                            <p class="text--sm mt--20">
                                Go to <strong>Settings</strong> > <strong>Integrations</strong> >  <strong>Integrations directory</strong> 
                            </p>
                        </div>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton 
                        label="Cancel" variant="outline" size="sm"
                        @submit="modalAction = ''"
                    />
                    <BaseButton label="Add integration" size="sm" @submit="goToIntegrationPage" />
                </div>
            </template>
        </MainModal>

        <CreateClientCheckInModal 
            :show-modal="modalAction === 'showCheckInModal'" 
            :current-client="currentClient"
            @complete=""
            @close="modalAction = ''" 
        />

        <UpgradeAccountAlt 
            :show-modal="modalAction === 'showUpgradeModalAlt'"    
            title="Set check-in reminders with your client" 
            subtitle="This is a great way to make sure your clients have you at top of mind when scouting for creators to provide services." 
            show-paid-plan-badge
            :available-plans="['plus']"
            @cancel="modalAction = ''"
        />

        <MainModal 
            :show-modal="modalAction === 'showSendModal'"
            size="md" 
            title="Send rate card to client" 
            @close="resetCloseSendToClient"
        >
            <template #body>
                <div>
                    <Alert type="primary" classes="text--sm">
                        <template #text>
                            <p class="text--xs">
                                Use this option to share this rate card as an email with clients using a pre-written message template.
                            </p>
                        </template>
                    </Alert>

                    <form ref="formRef" :class="{ 'rc__form': formHeight >= 300 }" @submit.prevent="">
                        <div class="mb--20 flex-1">
                            <FormLabel title="Select rate card" html-for="rateType" />
                            <CustomDropdown 
                                id="rateCardsDropdown" 
                                v-model="rateCard" 
                                :options="rateCards"
                                list-type="rate cards" 
                                index-label="title"
                                :loading="loadingState === 'rateCards'"
                                :fetch-list-on-show="!rateCards.length"
                                @search="handleFetchRateCards"
                                @select="v => rateCard = v" 
                                @fetch-list-show="handleFetchRateCards"
                            />
                        </div>

                        <div class="mb--15">
                            <FormLabel title="Subject" html-for="subject" />
                            <p class="form-control form__input--none form-control-sm disabled">
                                {{ `Rate card for ${rateCard.title}` }}
                            </p>
                        </div>

                        <template v-if="rateCard.title">
                            <div class="mt--20 mb--5 flex align-items-center justify-content-between">
                                <FormLabel title="What the client sees:" label-type="menu" class="mb--0 text--upper" html-for="subject" />
                                <BaseButton 
                                    v-if="ccEmails && ccEmails.length"
                                    :label="!ccEmails.length ? '+ Add CC Emails': '- Remove CC Emails'" 
                                    class="p--0"
                                    bold
                                    variant="ghost"
                                    @submit="toggleCCEmails"
                                />
                            </div>

                            <div class="rc__preview">
                                <div class="rc__preview__text flex align-items-center">
                                    <span class="text--medium">To:</span>
                                    <span class="ml--5">{{ currentClient.email }}</span>
                                </div>

                                <div v-if="ccEmails.length" class="flex flex-wrap rc__preview__text">
                                    <p class="text--medium">
                                        CC:
                                    </p>
                                    <p v-for="(email, index) in ccEmails" :key="email" class="rc__preview__text ml--5">
                                        {{ email }}{{ index + 1 >= ccEmails.length ? '' : ',' }}
                                    </p>
                                </div>

                                <p class="rc__preview__text">
                                    Hello {{ currentClient.name }},
                                </p>

                                <p class="rc__preview__text">
                                    Please find attached my rate card outlining service offerings and pricing structure as requested.
                                </p>

                                <p class="rc__preview__text">
                                    These rates reflect my professional expertise and the quality of work I deliver to clients. Please review the attached information and let me know if you have any questions.
                                </p>

                                <p class="rc__preview__text">
                                    I would love to discuss how these services align with your project requirements and look forward to potential collaboration.
                                </p>

                                <p class="rc__preview__text">
                                    Best regards,
                                </p>
                                        
                                <p class="rc__preview__text mb--0">
                                    {{ userProfile.fullName }}
                                </p>
                            </div>
                        </template>
                    </form>
                </div>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <Base-Button
                        label="Cancel" 
                        variant="outline" 
                        :disabled="loadingState === 'sendRateCard'"
                        @submit="resetCloseSendToClient"
                    />
                    <Base-Button 
                        label="Send email"
                        class="ml--5"
                        :loading="loadingState === 'sendRateCard'" 
                        type="submit" 
                        @submit="sendEmail" 
                    />
                </div>
            </template>
        </MainModal>

        <MainModal 
            :show-modal="modalAction === 'showAddLinkModal' || modalAction === 'showEditLinkModal'"
            size="md" 
            :title="modalAction === 'showAddLinkModal' ? 'New link' : 'Edit link'" 
            @close="closeLinkModal"
        >
            <template #body>
                <form @submit.prevent="">
                    <div class="form__row">
                        <FormInput 
                            id="linkName" 
                            v-model="linkForm.name"
                            type="text" 
                            size="sm" 
                            wrapper-class="flex-1"
                            info-type="required"
                            no-margin-bottom
                            html-for="linkName" 
                            label="Link name"
                        />
                        <FormInput 
                            id="linkUrl" 
                            v-model="linkForm.url"
                            type="text" 
                            size="sm" 
                            wrapper-class="flex-1"
                            info-type="required"
                            no-margin-bottom
                            html-for="linkUrl" 
                            label="Link URL"
                        />
                    </div>
                </form>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton 
                        class="mr--5" 
                        label="Cancel" 
                        variant="outline" 
                        :disabled="loadingState === 'link_loading'" 
                        @submit="closeLinkModal"
                    />
                    <BaseButton 
                        type="submit" 
                        :loading="loadingState === 'link_loading'" 
                        :disabled="!linkForm.name || !linkForm.url" 
                        label="Save" 
                        @submit="manageLinks"
                    />
                </div>
            </template>
        </MainModal>
    </div>
</template>

<script>
import {
    IconSvg,
    EmptyPage,
    ConfirmDeletion,
    PageSpinner,
    BaseButton,
    CreateClientModal,
    Status,
    FormLabel,
    NonLinkedTabs,
    NonLinkedTab,
    ArchiveRecord, 
    UpgradeAccountModal, 
    CreateMeetingModal,
    FollowUpEmailModal, 
    MainModal,
    List,
    ListItem,
    CreateClientCheckInModal,
    Alert,
    CustomDropdown,
} from "@/components/ui/index";
import { ClientInvoices, ClientNotes, ClientProjects, ClientMeetings, ClientCheckIns, ClientDocuments } from "@/components/clients/index";
import { industryData, clientTabList } from "@/utils/dummy";
import { CLIENT_STATUSES, MAX_COUNT_BY_COLLECTIONS, MAX_COUNT_BY_FEATURES } from "@/utils/enums";
import UpgradeAccountAlt from "@/components/ui/modals/UpgradeAccountAlt.vue";
import FormInput from "@/components/ui/input/FormInput.vue";

export default {
    name: "ClientDetailsView",

    components: {
        EmptyPage, 
        CreateClientCheckInModal,
        CreateMeetingModal,
        IconSvg,
        BaseButton,
        PageSpinner, 
        MainModal,
        ConfirmDeletion, 
        UpgradeAccountModal,
        NonLinkedTabs,
        NonLinkedTab, 
        FormLabel,
        ClientMeetings,
        ClientInvoices,
        ClientNotes,
        ClientProjects,
        ArchiveRecord,
        CreateClientModal,
        Status,
        FollowUpEmailModal,
        List,
        ListItem,
        ClientDocuments,
        Alert,
        CustomDropdown,
        UpgradeAccountAlt,
        FormInput,
    },

    data() {
        return {
            CLIENT_STATUSES,
            pageLoading: false,
            pageLoadingErr: false,
            industries: industryData,
            editModalType: "",
            deleteClient: {
                loading: false,
                err: false,
            },
            loading: false,          
            clientTabList,
            currentClient: {
                email: "",
                ccEmails: [],
            },
            duplicateLoading: false,
            updateNameLoading: false,
            currentTab: undefined,
            showUpgradeModalAlt: false,
            upgradeType: "clients", // "clients" | "client check-ins"
            isSuggestionDisabled: false,
            selectedMeeting: null,
            modalAction: "",
            fetchCalendarsState: "",
            refreshMeetingList: false,
            formHeight: 0,
            ccEmails: [],
            loadingState: "",
            rateCard: undefined,
            rateCards: [],
            links: [],
            linkIdToDelete: "",
            linkForm: {
                url: "",
                name: "",
            },
        };
    },

    computed: {
        userProfile() {
            return this.$store.getters["auth/profile"];
        },

        showUpgradeModal() {
            return this.$store.getters["app/showUpgradeModal"];
        },

        checkUpgradeLoading() {
            return this.$store.getters["app/checkUpgradeLoading"];
        },

        checkUpgradeFields() {
            return this.$store.getters["app/checkUpgradeFields"];
        },

        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },

        appIntegrations() {
            return this.$store.getters["config/integrations"] || [];
        },

        userIntegrationsMap() {
            const newMap = new Map();
            const integrations = this.$store.getters["integration/integrations"] || [];

            for (let i = 0; i < integrations.length; i++) {
                const item = integrations[i];

                if (["google_calendar"].includes(item.key) && item.enabled) {
                    newMap.set(item.key, {
                        _id: item._id,
                        enabled: item.enabled,
                    });
                }
            } 

            return newMap;
        },

        calendarIntegrations() {
            return this.appIntegrations.filter(item => this.userIntegrationsMap.get(item?.key));
        },

        meetings() {
            return this.$store.getters["meeting/meetings"];
        },

        rateCardsStore() {
            return this.$store.getters["ratecards/rateCards"];
        },
    },

    async mounted() {
        await this.handleFetchClient();

        if (!this.appIntegrations.length) {
            await this.loadIntegrations();
        }

        if (this.rateCardsStore.length) {
            this.rateCards = this.rateCardsStore;
        }

        // Observe the form element
        if (this.$refs.formRef) {
            this.resizeObserver.observe(this.$refs.formRef);
            // Get initial height
            this.formHeight = this.$refs.formRef.offsetHeight;
        }

        if (typeof this.currentClient.ccEmails === "string" && this.currentClient.ccEmails) {
            this.ccEmails = this.currentClient.ccEmails.split(",").map(email => email.trim());
        } else if (Array.isArray(this.currentClient.ccEmails)) {
            this.ccEmails = this.currentClient.ccEmails;
        } else {
            this.ccEmails = [];
        }
    },

    methods: {
        setClientTab(tab) {
            this.currentTab = tab;
        },

        goToIntegrationPage() {
            return this.$router.push("/dashboard/settings/integrations");
        },

        async handleFetchClient() {
            try {
                this.pageLoadingErr = false;
                this.pageLoadingErrMsg = "";
                this.pageLoading = true;

                const res = await this.$store.dispatch("clients/getClient", this.$route.params.id);
                this.currentClient = {
                    ...res,
                    ccEmails: res.ccEmails ? res.ccEmails.split(",") : [],
                };
                this.links = res?.links || [];
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
               
                this.pageLoadingErr = true;
                this.pageLoadingErrMsg = errMsg;
            } finally {
                this.pageLoading = false;
            }
        },

        async handleDeleteClient() {
            try {
                const id = this.currentClient?._id || this.$route.params.id;
                
                this.deleteClient.err = false;
                this.deleteClient.loading = true;

                await this.$store.dispatch("clients/deleteClients", [`${id}`]);
                
                // don't await
                this.$toast.success("Client deleted");

                return this.$router.push("/dashboard/clients");
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                
                this.deleteClient.err = true;
                this.$toast.error(errMsg);
            } finally {
                this.deleteClient.loading = false;
            }
        },

        async handleArchiveClient() {
            try {
                this.loadingState = "archive_loading";

                const id = this.currentClient?._id || this.$route.params.id;

                if (!id) {
                    this.$toast.error("No client id passed");
                    return;
                }

                await this.$store.dispatch("clients/archiveClient", id);
                this.$toast.success("Client archived");                
                return this.$router.push({ path: "/dashboard/clients" });
            } catch (error) {
                const errMsg = error?.response?.data?.message || error.message;
                
                this.$toast.error(errMsg);
            } finally {
                this.loadingState = "";
            }
        },

        openDetailsModal(type) {
            this.editModalType = type;
            this.modalAction = "showEditModal";
        },

        resetEditModal() {  
            this.editModalType = "";
            this.modalAction = "";
        },

        async updateClientForm({ payload, resetFn }) {
            try {
                if (!this.currentClient._id) {
                    throw new Error("Client id is required");
                }

                this.loadingState = "update_loading";
                
                const res = await this.$store.dispatch("clients/updateClient", payload);
                this.currentClient = { ...this.currentClient, ...res };
                this.currentClient.ccEmails = res.ccEmails?.split(",");
                
                resetFn();
                this.modalAction = "";
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.loadingState = "";
            }
        },
        
        async handleStatusUpdate(val) {
            try {
                if (val === this.currentClient.status) return;

                this.loading = true;

                const res = await this.$store.dispatch("clients/updateClient", { _id: this.$route.params.id, status: val });
                this.handleDetailsUpdate(res);
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.loading = false;
            }
        },

        async handleNameUpdate(event) {
            try {
                const value = event.target.value.trim();
                
                if (value === this.currentClient.name) return;

                this.updateNameLoading = true;

                const res = await this.$store.dispatch("clients/updateClient", { _id: this.$route.params.id, name: value });
                this.handleDetailsUpdate(res);
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.updateNameLoading = false;
            }
        },

        async sendFollowUp() {
            try {
                this.followUpLoading = true;

                const payload = {
                    email: this.currentClient.email || "",
                };

                await this.$store.dispatch("clients/followUpWithClient", payload);
            } catch (error) {
                throw error;
            } finally {
                this.followUpLoading = false;
            }
        },

        handleDetailsUpdate(data) {
            this.currentClient = {
                ...this.currentClient,
                ...data,
                ccEmails: data.ccEmails ? data.ccEmails.split(",") : [],
            };

            this.modalAction = "";
        },

        closeUpgradeModal() {
            if (this.showUpgradeModalAlt) {
                this.upgradeType = "";     
                this.showUpgradeModalAlt = false;
                return;
            }

            this.$store.dispatch("app/closeUpgradeModal", "clients");
        },

        async handleDuplicateClient() {
            try {
                this.duplicateLoading = true;
                await this.$store.dispatch("app/checkUpgradeStatus", "clients");

                const res = await this.$store.dispatch("clients/duplicateClient", {
                    ...this.currentClient,
                    ccEmails: this.currentClient.ccEmails.join(","),
                });

                if (!res._id) {
                    this.$toast.error("Unable to duplicate client");  
                    return;      
                }

                this.$toast.success("Client duplicated");  
                return this.$router.push(`/dashboard/clients/${res._id}`);
            } catch (error) {
            } finally {
                this.duplicateLoading = false;
            }
        },

        async openCheckInModal() {
            try {
                if (["standard", "free"].includes(this.accountPlan)) {   
                    this.modalAction = "showUpgradeModalAlt";    
                    return;
                }

                this.modalAction = "showCheckInModal";
            } catch (error) {
                // console.log({ error });
            }
        },

        resetCloseSendToClient() {
            this.rateCard = undefined;
            this.modalAction = "";
        },

        async sendEmail() {
            try {    
                if (!this.currentClient._id || !this.form._id) {
                    this.$toast.error("Select a client to continue");
                    return;
                }
                
                this.loadingState = "sendRateCard";

                const payload = {
                    rateCardId: this.rateCard._id,
                    clientId: this.currentClient._id,
                    plan: this.accountPlan,
                    clientEmail: this.currentClient.email,
                    clientName: this.currentClient.name,
                    template: "Template1.1",
                };
    
                await this.$store.dispatch("ratecards/sendClientRateCard", payload);

                this.$toast.success("Email sent");
                this.resetCloseSendToClient();
            } catch (error) {
                const errMsg = error?.response?.data?.message || error.message;
                this.$toast.error(errMsg);
            } finally {
                this.loadingState = "";
            }
        },

        toggleCCEmails() {
            if (this.ccEmails.length) {
                this.ccEmails = [];
            } else {
                this.ccEmails = this.client.ccEmails || [];
            }
        },

        openSendCard() {
            if (this.accountPlan === "free" || this.accountPlan === "standard") {
                this.modalAction = "showUpgradeModalSendRateCard";
                return;
            }

            this.modalAction = "showSendModal";
        },

        async openCreateMeeting() {
            try {
                if (["standard", "free"].includes(this.accountPlan)) {        
                    const maxLimit = MAX_COUNT_BY_COLLECTIONS[this.accountPlan].meetings.create;
    
                    if (this.meetings.length >= maxLimit) {
                        this.showUpgradeModalAlt = true;
                        return;
                    }
                }
            
                // if user still has no active calendar integrations
                if (!this.calendarIntegrations.length) {
                    this.modalAction = "showIntegrationInfoModal";
                    return;
                }

                this.modalAction = "showScheduleMeetingModal";
            } catch (error) {
                const errMsg = error?.response?.data?.message || error.message;
                this.$toast.error(errMsg);
            }
        },

        closeMeetingModal() {
            this.modalAction = "";
        },

        resetMeetingListUpdate() {
            this.refreshMeetingList = false;
            this.selectedMeeting = undefined;
        },

        async onCompleteCreateMeeting(resp) {  
            this.refreshMeetingList = true;
            this.selectedMeeting = resp;
            this.closeMeetingModal();
        },

        async loadIntegrations() {
            try {
                this.fetchCalendarsState = "loading";

                await Promise.all([
                    this.$store.dispatch("config/fetchSystemIntegrations", { refresh: true }),
                    this.$store.dispatch("integration/fetchUserIntegrations", { refresh: true }),
                ]);
            } catch (error) {                
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);

                this.fetchCalendarsState = "error";
            } finally {
                this.fetchCalendarsState = "default";
            }
        },

        async handleFetchRateCards() {
            try {
                this.loadingState = "rateCards";

                const pageQueryObj = {
                    page: 1,
                };

                this.rateCards = await this.$store.dispatch("ratecards/fetchRateCards",  {
                    payload: pageQueryObj, 
                    refresh: true,
                    external: true,
                });
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.loadingState = "";
            }
        },

        openEditLinkModal(item) {
            this.linkForm = item;
            this.modalAction = "showEditLinkModal";
        },

        closeLinkModal() {
            this.linkForm = {
                name: "",
                url: "",
            };
            this.modalAction = "";
        },

        async manageLinks() {            
            try {                
                if (!this.$route.params.id) {
                    this.$toast.error("Unable to update link");
                    return;
                }

                this.loadingState = "link_loading";

                const res = await this.$store.dispatch("clients/manageLinks", {
                    clientId: this.$route.params.id,
                    payload: this.modalAction === "showAddLinkModal" ? {
                        name: this.linkForm.name,
                        url: this.linkForm.url,
                    } : {
                        name: this.linkForm.name,
                        url: this.linkForm.url,
                        _id: this.linkForm?._id,
                    },
                    params: this.modalAction === "showAddLinkModal" ? { type: "add" } : { type: "update" },
                });
                
                this.links = res;
                this.closeLinkModal();
            } catch (error) {
                this.loadingState = "link_error";
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.loadingState = "";
            }
        },

        async deleteLink(id) {            
            try {                                
                if (!id || !this.$route.params.id) {
                    this.$toast.error("Unable to delete link");
                    return;
                }

                this.linkIdToDelete = id;
                this.loadingState = "delete_link_loading";
                this.links = await this.$store.dispatch("clients/manageLinks", {
                    clientId: this.$route.params.id,
                    payload: { linkId: id },
                    params: { type: "delete" },
                });

            } catch (error) {
                this.loadingState = "delete_link_error";
            } finally {
                this.loadingState = "";
            }
        },

        async addLink() {            
            try {                
                if (!this.$route.params.id) {
                    this.$toast.error("Unable to add link");
                    return;
                }

                this.loadingState = "add_link_loading";
                
                const res = await this.$store.dispatch("clients/manageLinks", {
                    clientId: this.$route.params.id,
                    payload: {
                        name: this.linkForm.name,
                        url: this.linkForm.url,
                    },
                    params: { type: "add" },
                });
                
                this.links = res;
                this.linkForm = {
                    name: "",
                    link: "",
                };
            } catch (error) {
                this.loadingState = "add_link_error";
                const errMsg = error?.response?.data?.message || error?.message;                
                this.$toast.error(errMsg);
            } finally {
                this.loadingState = "";
            }
        },
    },
};
</script>

<style lang="scss" scoped>
    .dropdown-item {
        display: flex;
        align-items: center;
        cursor: pointer;

        &:last-of-type {
            padding-bottom: 0.1rem !important;
        }
    }
</style>